import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff, Copy, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  salary: string;
  description: string;
  requirements: string[];
  isActive: number;
  postingDate?: string | null;
  expiryDate?: string | null;
  uniqueId?: string;
  createdAt: string;
  updatedAt: string;
}

const JobsList: React.FC = () => {
  const { token } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/jobs/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const data = await res.json();
      setJobs(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/jobs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to delete job');
      
      setJobs(jobs.filter((job) => job.id !== id));
      alert('Job posting deleted successfully!');
    } catch (err: any) {
      alert('Error deleting job: ' + err.message);
    }
  };

  const toggleActive = async (job: Job) => {
    try {
      const res = await fetch(`${BACKEND_URL}/jobs/${job.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...job, isActive: job.isActive ? 0 : 1 }),
      });
      if (!res.ok) throw new Error('Failed to update job');
      
      setJobs(jobs.map((j) => (j.id === job.id ? { ...j, isActive: j.isActive ? 0 : 1 } : j)));
    } catch (err: any) {
      alert('Error updating job: ' + err.message);
    }
  };

  const filteredJobs = jobs.filter((job) => {
    if (filter === 'active') return job.isActive === 1;
    if (filter === 'inactive') return job.isActive === 0;
    return true;
  });

  const computeStatusLabel = (job: Job): { label: string; variant: 'active' | 'upcoming' | 'expired' | 'inactive' } => {
    const today = new Date().toISOString().slice(0, 10);
    if (!job.isActive) return { label: 'Inactive', variant: 'inactive' };
    const posting = job.postingDate || '';
    const expiry = job.expiryDate || '';

    if (posting && posting > today) {
      return { label: `Scheduled (${posting})`, variant: 'upcoming' };
    }
    if (expiry && expiry < today) {
      return { label: `Expired (${expiry})`, variant: 'expired' };
    }
    return { label: 'Active', variant: 'active' };
  };

  const copyApplicationUrl = (uniqueId: string) => {
    const url = `${window.location.origin}/apply/${uniqueId}`;
    navigator.clipboard.writeText(url).then(() => {
      alert('Application URL copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Application URL copied to clipboard!');
    });
  };

  if (loading) {
    return (
      <div 
        className="text-center py-8" 
        style={{ 
          color: 'var(--pw-muted-light)',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        Loading jobs...
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="text-center py-8" 
        style={{ 
          color: '#ef4444',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 
            className="text-3xl font-bold mb-2" 
            style={{ 
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.02em'
            }}
          >
            Job Postings
          </h1>
          <p 
            className="text-sm" 
            style={{ 
              color: 'var(--pw-muted-light)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            {filteredJobs.length} total job(s)
          </p>
        </div>
        <Link
          to="/admin/jobs/create"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all duration-200 font-medium shadow-lg"
          style={{ 
            background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)',
            color: 'var(--pw-text-main)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(124, 58, 237, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
          }}
        >
          <Plus className="w-5 h-5" />
          Create New Job
        </Link>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {(['all', 'active', 'inactive'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-5 py-2.5 rounded-xl font-medium capitalize transition-all duration-200"
            style={{
              ...(filter === f
                ? { backgroundColor: 'var(--pw-primary)', color: 'var(--pw-text-main)' }
                : { backgroundColor: 'var(--pw-surface-tertiary)', color: 'var(--pw-text-main)' }
              ),
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              if (filter !== f) {
                e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (filter !== f) {
                e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
              }
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <div 
          className="rounded-xl p-12 text-center"
          style={{ 
            background: 'var(--pw-gradient-card)',
            border: '1px solid var(--pw-surface-tertiary)',
            boxShadow: 'var(--pw-shadow)'
          }}
        >
          <p 
            className="mb-4" 
            style={{ 
              color: 'var(--pw-muted-light)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            No job postings found.
          </p>
          <Link
            to="/admin/jobs/create"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all duration-200 font-medium shadow-lg"
            style={{ 
              background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Plus className="w-5 h-5" />
            Create Your First Job
          </Link>
        </div>
      ) : (
        <div 
          className="rounded-2xl overflow-hidden backdrop-blur-sm"
          style={{ 
            background: 'var(--pw-gradient-card)',
            border: '1px solid var(--pw-surface-tertiary)',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <table className="w-full">
            <thead style={{ backgroundColor: 'var(--pw-surface-secondary)' }}>
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Title
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Department
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Location
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Application URL
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Status
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody style={{ borderTop: '1px solid var(--pw-surface-tertiary)' }}>
              {filteredJobs.map((job) => (
                <tr 
                  key={job.id} 
                  style={{ borderBottom: '1px solid var(--pw-surface-tertiary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td className="px-6 py-4">
                    <div 
                      className="font-medium" 
                      style={{ 
                        color: 'var(--pw-text-main)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      {job.title}
                    </div>
                    <div 
                      className="text-sm" 
                      style={{ 
                        color: 'var(--pw-muted-light)',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                      }}
                    >
                      {job.type}
                    </div>
                  </td>
                  <td 
                    className="px-6 py-4" 
                    style={{ 
                      color: 'var(--pw-text-main)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    {job.department}
                  </td>
                  <td 
                    className="px-6 py-4" 
                    style={{ 
                      color: 'var(--pw-text-main)',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                  >
                    {job.location}
                  </td>
                  <td className="px-6 py-4">
                    {job.uniqueId ? (
                      <div className="flex items-center gap-2">
                        <a
                          href={`/apply/${job.uniqueId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:text-blue-300 underline truncate max-w-[150px]"
                          style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          /apply/{job.uniqueId}
                        </a>
                        <button
                          onClick={() => copyApplicationUrl(job.uniqueId!)}
                          className="p-1 rounded transition-colors"
                          style={{ color: 'var(--pw-secondary-light)' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Copy application URL"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <span 
                        className="text-xs" 
                        style={{ 
                          color: 'var(--pw-muted-light)',
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                        }}
                      >
                        Not available
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const status = computeStatusLabel(job);
                      let bg = 'var(--pw-surface-tertiary)';
                      let color = 'var(--pw-text-main)';
                      if (status.variant === 'active') bg = 'var(--pw-success)';
                      if (status.variant === 'upcoming') bg = 'var(--pw-secondary)';
                      if (status.variant === 'expired') bg = '#4b5563';
                      if (status.variant === 'inactive') {
                        bg = 'var(--pw-surface-tertiary)';
                        color = 'var(--pw-muted-light)';
                      }
                      return (
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: bg,
                            color,
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                          }}
                        >
                          {status.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/jobs/edit/${job.id}`}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--pw-secondary-light)' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => toggleActive(job)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--pw-text-main)' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={job.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {job.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(job.id)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: '#ef4444' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default JobsList;

